import {
  ArrayUtil,
  PropertyAtPath,
  PropertyPath,
} from '@suntex.core/animation';
import type { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';

import { InfoPane } from '../display/InfoPane';
import { Radial } from '../display/Radial';
import { StarValue } from '../types';

import { MAX_MODULE_PROGRESS } from './computeStarValue';

export function applyProgress(
  rootRadial: Radial,
  progressSummary: Entities_Player_ProgressSummary_Response,
  goldStarInfoPane: InfoPane,
): void {
  applyToRadial(rootRadial);

  // Recursively apply starring to all descendants
  function applyToRadial(radial: Radial): StarValue {
    let star: StarValue;
    let progress: number;

    // If this radial has children, recurse
    if (radial.radialChildren.length > 0) {
      let goldStarCount = 0;
      let silverStarCount = 0;
      let totalCount = 0;

      for (const child of radial.radialChildren.filter((r) => r.inFocus)) {
        const childStar = applyToRadial(child);
        totalCount++;
        if (childStar === StarValue.Gold) {
          goldStarCount++;
          silverStarCount++;
        } else if (childStar === StarValue.Silver) {
          silverStarCount++;
        }
      }

      if (goldStarCount > 0 && radial.infoPane == null) {
        radial.setInfoPane(goldStarInfoPane);
      } else if (goldStarCount === 0 && radial.infoPane === goldStarInfoPane) {
        radial.setInfoPane(null);
      }

      if (goldStarCount === totalCount) {
        star = StarValue.Gold;
        progress = StarValue.Gold;
      } else if (silverStarCount === totalCount) {
        star = StarValue.Silver;
        progress = StarValue.Silver + goldStarCount / totalCount;
      } else {
        star = StarValue.None;
        progress = silverStarCount / totalCount;
      }
    } else {
      // Leaf node - compute fractional progress for animation, derive star from it
      progress = computeLeafProgress(progressSummary, radial);
      star = Math.floor(progress);
    }

    radial.showProgress(progress);
    return star;
  }
}

/**
 * Computes the raw fractional progress value (0–MAX_MODULE_PROGRESS) for a leaf radial.
 * Used internally to drive the animated star fill in the hub canvas.
 */
function computeLeafProgress(
  progressSummary: Entities_Player_ProgressSummary_Response,
  radial: Radial,
): number {
  if (!radial.dataPath && !radial.moduleSlug) return 0;

  const path: PropertyPath<Entities_Player_ProgressSummary_Response> =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    radial.dataPath ?? ['games', radial.moduleSlug!];

  const data = ArrayUtil.getProperty(progressSummary, path) as PropertyAtPath<
    Entities_Player_ProgressSummary_Response,
    typeof path
  >;

  if (typeof data !== 'number') return 0;
  return Math.max(
    0,
    Math.min(data / (radial.dataTarget ?? 1), MAX_MODULE_PROGRESS),
  );
}
